'use client';

import type React from 'react';
import { useEffect, useMemo, useState } from 'react';

import Image from 'next/image';
import { useShallow } from 'zustand/react/shallow';

import Emphasis from 'DesignSystem3/Text/Emphasis';

import { Palette_DarkMode, Palette_LightMode } from 'DesignSystem2/Theme/Palette/PaletteVariations';
import { eventImageUrl, eventTitle } from 'queries/marketEntityTransform';
import type { EventEntity, MarketEntity } from 'queries/marketEntityTransform';
import { useHydrateEventsIntoStore, useHydrateSeriesIntoStore } from 'queries/markets';
import { useMarketEntitiesStore } from 'stores/zustand/marketEntitiesStore';
import config from 'utils/config';

import { resolveTickerSpeed } from '../../lib/embedParams';
import { getEventRelativeUrl } from 'utils/events';
import KalshiLogo from 'components/DesignSystem/Icons/adhoc/KalshiLogo';

interface KalshiNewsScrollBannerProps {
    EVENT_TICKERS?: string[];
    updateInterval?: number;
    /** Scroll-speed multiplier: >1 scrolls faster, <1 slower. 1 keeps the default pace. */
    speed?: number;
    showBranding?: boolean;
    maxItems?: number;
    isDark?: boolean;
    forceRestart?: boolean;
    onEventsLoaded?: (events: EventEntity[]) => void;
    logoColor?: string;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    referral?: string;
}

interface KalshiNewsScrollBannerWrapperProps extends KalshiNewsScrollBannerProps {
    id?: string;
}

const DEFAULT_EVENT_TICKERS = [
    'KXBTCMAXY-25',
    'KXGENIUS-25SEP',
    'KXBTCRESERVE-26',
    'KXSHUTDOWNBY-25',
    'KXGDPYEAR-25',
    'KXWMENSINGLES-25',
    'KXNEWPARTYMUSK',
    'SENATENC-26',
    'KXPRESCHILE-25',
];

interface KalshiNewsScrollBannerCoreProps extends KalshiNewsScrollBannerProps {
    isVisible?: boolean;
}

const KalshiNewsScrollBanner = ({
    EVENT_TICKERS = DEFAULT_EVENT_TICKERS,
    updateInterval = 10000, // 10 seconds
    speed = 1,
    maxItems = 12,
    isDark = true,
    isVisible = true,
    showBranding = true,
    onEventsLoaded,
    logoColor,
    utmSource,
    utmMedium,
    utmCampaign,
    referral,
}: KalshiNewsScrollBannerCoreProps) => {
    const Palette = useMemo(() => (isDark ? Palette_DarkMode : Palette_LightMode), [isDark]);
    const eventTickers = useMemo(() => EVENT_TICKERS.slice(0, maxItems), [EVENT_TICKERS, maxItems]);

    // Fetch + keep events fresh on updateInterval while visible — replaces the manual
    // marketStore.loadEvents()/setInterval loop.
    useHydrateEventsIntoStore(eventTickers, {
        enabled: isVisible,
        refetchInterval: isVisible ? updateInterval : false,
    });
    const eventsByTicker = useMarketEntitiesStore(
        useShallow((s) => Object.fromEntries(eventTickers.map((t) => [t, s.events[t]]))),
    );
    const events = useMemo(
        () => eventTickers.map((t) => eventsByTicker[t]).filter((e): e is EventEntity => e !== undefined),
        [eventTickers, eventsByTicker],
    );

    const marketTickers = useMemo(() => [...new Set(events.flatMap((event) => event.marketTickers))], [events]);
    const marketsByTicker = useMarketEntitiesStore(
        useShallow((s) => Object.fromEntries(marketTickers.map((t) => [t, s.markets[t]]))),
    );

    // Ensure series metadata is loaded so URLs include the series title.
    const seriesTickers = useMemo(
        () => [...new Set(events.map((event) => event.seriesTicker).filter(Boolean))],
        [events],
    );
    useHydrateSeriesIntoStore(seriesTickers);
    const seriesByTicker = useMarketEntitiesStore(
        useShallow((s) => Object.fromEntries(seriesTickers.map((t) => [t, s.series[t]]))),
    );

    useEffect(() => {
        if (events.length > 0) {
            onEventsLoaded?.(events);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [events]);

    const formatPrice = (price: number): string => {
        return `${price}%`;
    };

    const linkQueryString = useMemo(() => {
        const linkParams = new URLSearchParams();
        if (utmSource) linkParams.set('utm_source', utmSource);
        if (utmMedium) linkParams.set('utm_medium', utmMedium);
        if (utmCampaign) linkParams.set('utm_campaign', utmCampaign);
        if (referral) linkParams.set('referral', referral);
        const query = linkParams.toString();
        return query ? `?${query}` : '';
    }, [utmSource, utmMedium, utmCampaign, referral]);

    // One pass over the strip. `speed` is a multiplier rather than a duration so the pace
    // still scales with how many markets are on the strip.
    const scrollDurationSeconds = useMemo(() => {
        const base = Math.max(events.length * 3, 20);
        return Math.round(base / resolveTickerSpeed(speed));
    }, [events.length, speed]);

    const newsItems = useMemo(() => {
        return events.map((event, index) => {
            const eventMarkets = event.marketTickers
                .map((t) => marketsByTicker[t])
                .filter((m): m is MarketEntity => m !== undefined);
            if (eventMarkets.length === 0) return null;

            // Find the most relevant market based on volume and price
            // For Yes/No markets, show the higher probability
            // For categorical markets, show the market with highest volume
            const marketsWithPrice = eventMarkets.filter((m) => m.lastPrice > 0);
            if (marketsWithPrice.length === 0) return null;

            let selectedMarket = marketsWithPrice[0];

            if (marketsWithPrice.length > 1) {
                // Sort by volume first to get most actively traded market
                const sortedByVolume = [...marketsWithPrice].sort((a, b) => b.lastPrice - a.lastPrice);
                selectedMarket = sortedByVolume[0];
            }

            const market = selectedMarket;
            const displayTitle = eventTitle(event);
            const series = seriesByTicker[event.seriesTicker];
            const seriesTitle = series?.title;
            const eventUrl = `${config.baseUrl}${getEventRelativeUrl(
                event.seriesTicker,
                seriesTitle,
                event.ticker,
            )}${linkQueryString}`;

            // Determine which price and strike to show
            let displayPrice = market.lastPrice;
            let strikeLabel = market.yesSubtitle || 'Yes';

            // For standard Yes/No markets without specific subtitles, show the higher probability
            if (!market.yesSubtitle) {
                const no_bid = 100 - market.yesBid; // Calculate no_bid as inverse
                if (no_bid > market.yesBid) {
                    displayPrice = no_bid;
                    strikeLabel = 'No';
                }
            }

            return (
                <a
                    key={`${event.ticker}-${index}`}
                    href={eventUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-4 px-4 flex-shrink-0 transition-opacity border-r border-stroke-x30 group hover:opacity-80"
                >
                    <div className="flex-shrink-0 w-8 h-8 overflow-hidden bg-container-x40 rounded-md">
                        <Image
                            src={eventImageUrl(event, series)}
                            alt={displayTitle}
                            height={32}
                            width={32}
                            // The strip scrolls horizontally, so a lazy avatar is only fetched
                            // once it has already animated into view and renders as an empty
                            // box until it arrives. There are at most `maxItems` of them at
                            // 32px, so fetch them all up front.
                            loading="eager"
                            className="object-cover w-full h-full"
                        />
                    </div>
                    <div className="flex flex-col flex-shrink-0 max-w-[600px] overflow-hidden">
                        <Emphasis.x20 className="leading-none truncate group-hover:text-[var(--brand-primary)] transition-colors text-fill-x10">
                            {displayTitle}
                        </Emphasis.x20>

                        <Emphasis.x10 className="truncate text-fill-x10">{strikeLabel}</Emphasis.x10>
                    </div>
                    <Emphasis.x30 className="flex-shrink-0 whitespace-nowrap text-brand-primary">
                        {formatPrice(displayPrice)}
                    </Emphasis.x30>
                </a>
            );
        });
    }, [events, marketsByTicker, seriesByTicker, linkQueryString]);

    return (
        <div className="h-full py-1 overflow-hidden border rounded-md" style={{ backgroundColor: Palette.Surface.x10 }}>
            <div className="flex items-center h-full">
                {/* Logo section */}
                {showBranding && (
                    <div className="flex items-center justify-center px-3">
                        <KalshiLogo color={logoColor ?? Palette.Brand.Primary} height="20px" />
                    </div>
                )}

                {/* Scrolling content */}
                <div className="relative flex-1 overflow-hidden">
                    <div className="flex">
                        {/* Two copies back to back: the keyframe translates by -50%, so the
                            second copy is in place when the first scrolls out. Rendering the
                            same array in two sibling slots is not a duplicate-key collision:
                            React scopes keys per child slot, so each pass gets its own
                            namespace. Verified against React 19: a single array holding the same
                            keys twice does warn, this does not. */}
                        <div
                            className="flex whitespace-nowrap animate-scroll-infinite hover:[animation-play-state:paused] py-1.5"
                            style={{
                                animationDuration: `${scrollDurationSeconds}s`,
                                willChange: 'transform',
                            }}
                        >
                            {newsItems}
                            {newsItems}
                        </div>
                    </div>
                </div>

                <style jsx>{`
                    @keyframes scroll-infinite {
                        from {
                            transform: translateX(0);
                        }
                        to {
                            transform: translateX(-50%);
                        }
                    }

                    .animate-scroll-infinite {
                        animation-name: scroll-infinite;
                        animation-timing-function: linear;
                        animation-iteration-count: infinite;
                    }
                `}</style>
            </div>
        </div>
    );
};

const KalshiNewsScrollBannerWrapper: React.FC<KalshiNewsScrollBannerWrapperProps> = ({
    id = 'kalshi-news-scroll-banner',
    ...props
}) => {
    const [showBanner, setShowBanner] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const element = document.getElementById(id);
            if (element) {
                const rect = element.getBoundingClientRect();
                if (rect.top <= window.innerHeight && rect.bottom >= 0) {
                    setShowBanner(true);
                    window.removeEventListener('scroll', handleScroll);
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll();

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [id]);

    return <div id={id}>{showBanner && <KalshiNewsScrollBanner {...props} isVisible={showBanner} />}</div>;
};

export default KalshiNewsScrollBannerWrapper;
export { KalshiNewsScrollBanner };
